/** Physical contact, separate from the much wider ATC separation minima. */
import { FT_TO_M, distanceM, type AircraftCategory } from '@atc/core';
import { isRemovedPhase, type Aircraft } from './aircraft';

export interface Position { lat: number; lon: number; altitudeFt: number }
export interface Impact { a: Aircraft; b: Aircraft; lat: number; lon: number; altitudeFt: number; kind: 'air' | 'runway' | 'ground' }

// Approximate footprint and height of the rendered aircraft classes, in metres.
const SIZE: Record<AircraftCategory, { radius: number; height: number }> = {
  heavy: { radius: 31, height: 8 }, medium: { radius: 17, height: 5 }, light: { radius: 5, height: 2 },
};

/** Minimum separation during one fixed step; relative motion prevents tunnelling. */
export function detectImpacts(aircraft: readonly Aircraft[], before: ReadonlyMap<number, Position>): Impact[] {
  const out: Impact[] = [];
  const active = aircraft.filter((a) => !isRemovedPhase(a.phase));
  for (let i = 0; i < active.length; i++) for (let j = i + 1; j < active.length; j++) {
    const a = active[i]!, b = active[j]!;
    // Holding-point queues can be packed wingtip-close by authored airport data.
    // Contact requires movement; stationary aircraft are not a new incident.
    if (a.onGround && b.onGround && a.groundspeedKt < 1 && b.groundspeedKt < 1) continue;
    const ap = before.get(a.id) ?? a, bp = before.get(b.id) ?? b;
    const lat = (a.lat + b.lat + ap.lat + bp.lat) / 4;
    const mLat = 111_320, mLon = mLat * Math.cos(lat * Math.PI / 180);
    const r0x = (ap.lon - bp.lon) * mLon, r0y = (ap.lat - bp.lat) * mLat;
    const dx = ((a.lon - b.lon) * mLon) - r0x, dy = ((a.lat - b.lat) * mLat) - r0y;
    const dz = ((a.altitudeFt - b.altitudeFt) - (ap.altitudeFt - bp.altitudeFt)) * FT_TO_M;
    const r0z = (ap.altitudeFt - bp.altitudeFt) * FT_TO_M;
    const horizontal = SIZE[a.category].radius + SIZE[b.category].radius;
    const vertical = SIZE[a.category].height + SIZE[b.category].height;
    // A ground aircraft is already clamped to the airport surface; height is still MSL.
    const denom = (dx * dx + dy * dy) / (horizontal * horizontal) + dz * dz / (vertical * vertical);
    const t = denom ? Math.max(0, Math.min(1, -((r0x * dx + r0y * dy) / (horizontal * horizontal) + r0z * dz / (vertical * vertical)) / denom)) : 0;
    const x = r0x + dx * t, y = r0y + dy * t, z = r0z + dz * t;
    if ((x * x + y * y) / (horizontal * horizontal) + z * z / (vertical * vertical) > 1) continue;
    const ground = a.onGround && b.onGround;
    const kind = ground ? 'ground' : a.onGround || b.onGround ? 'runway' : 'air';
    out.push({ a, b, lat: (a.lat + b.lat) / 2, lon: (a.lon + b.lon) / 2, altitudeFt: (a.altitudeFt + b.altitudeFt) / 2, kind });
  }
  return out;
}
